import React from 'react';
import { SafeAreaProvider } from 'react-native-safe-area-context';
import { NavigationContainer } from '@react-navigation/native';
import {
    Institution,
    Config,
    ClockoutDataStore,
    User,
    StyleConfig,
    FeatureFlagStore,
    RNNavigationController,
    navigationRef,
    SplashScreen,
    ClockoutStack,
    bootstrapClockout,
    clockoutFontAssets,
} from '@clockout-dev/rn-ui';
import { installOpenSsoStorage, resetOpenSsoStorage } from './openSsoStorage';

let fontsRegistered = false;

// rn-ui text renders against weight keys the host must register. Its
// loadClockoutFonts() needs expo-font, which this build stubs out — so register
// the bundled assets directly via the browser FontFace API instead.
function registerRnUiFonts() {
    if (fontsRegistered || typeof document === 'undefined' || !document.fonts) return;
    fontsRegistered = true;
    Object.entries(clockoutFontAssets).forEach(([weight, src]) => {
        const url = typeof src === 'string' ? src : src?.default || src?.uri;
        if (!url) return;
        try {
            const face = new FontFace(weight, `url(${url})`);
            face.load().then((loaded) => document.fonts.add(loaded)).catch(() => {});
        } catch {
            /* ignore */
        }
    });
}

/**
 * The consumer app (rn-ui) embedded in the admin, booted to impersonate a user.
 *
 * Boots and mounts the same way the whitelabel hosts do since the FI
 * extensibility majors (core 2 / rn-ui 4): `bootstrapClockout` builds the
 * context and `ClockoutStack` renders rn-ui's default route table, so the embed
 * gets every route and flow a member's app has without a private copy of the
 * stack. The one difference is the boot: we set `clientId` directly (so core skips the SSN->id hash and uses
 * it on every API call) and pass a placeholder SSN — see open-sso-design-v2.md.
 * The `identity` fields (memberId + contact info) come from the admin's user
 * context endpoint; `memberId` is what lets the Advance screen load. SSN stays a
 * placeholder by design.
 *
 * Feature flags: `boot.featureFlags` is the member's service-resolved map (see
 * resolveSsoBoot). It is applied to core's FeatureFlagStore BEFORE the context
 * is built — the same pre-identity seed the whitelabel hosts do from their
 * native context — so routing-level flags (deposit-based onboarding, boost)
 * are live from the first frame, and it replaces whatever the PREVIOUS
 * impersonation left in the singleton store. core's `checkAuthenticated()`
 * then re-applies the server map on every session call; the server always
 * wins once the embed is talking to it. Deliberately NO `FeatureFlagProvider`
 * here — which is also why this mounts `ClockoutStack` in its own container
 * rather than `ClockoutNavigationRoot`, which wraps one: on web it would inject
 * localStorage into the flag store and bypass the per-boot storage isolation
 * below — the store's default already routes through core's storage seam,
 * which `installOpenSsoStorage` points at the in-memory map.
 *
 * @param {object} props
 * @param {object} props.boot - { apiKey, environment, serviceUrl, clientId, institution, style, identity, featureFlags }
 */
export default function OpenSsoApp({ boot }) {
    const [isReady, setIsReady] = React.useState(false);
    const [bootError, setBootError] = React.useState(null);

    React.useEffect(() => {
        let active = true;

        // Isolate core's storage from the admin's and wipe the prior
        // impersonation's cache, so this user's accounts never mix with the
        // last one's. Must run before ClockoutDataStore is constructed below,
        // since its constructor reloads from storage.
        installOpenSsoStorage();
        resetOpenSsoStorage();
        registerRnUiFonts();

        const identity = boot.identity || {};
        const name = [identity.givenName, identity.familyName].filter(Boolean).join(' ') || null;
        const featureFlags = boot.featureFlags || {};

        // Seed (and reset) the flag store for THIS member before any screen
        // mounts. The null clientId skips the offline-cache write — that
        // belongs to the per-member map core applies on its session call.
        FeatureFlagStore.Instance.applyServerMap(featureFlags, null);

        // bootstrapClockout also registers rn-ui's document picker (without it
        // the manual-upload file button does nothing) and core's navigation
        // adapter, and installs the copy tree. Three of its defaults are wrong
        // for the embed:
        //   - dataStore: it REUSES the previous boot's store when none is
        //     passed, which would hand this member the last one's accounts.
        //   - skipFonts: loadClockoutFonts() needs expo-font, which this build
        //     stubs out; registerRnUiFonts() above does it via FontFace.
        //   - skipAnalytics: see the Config comment below.
        const booted = bootstrapClockout({
            institution: new Institution(boot.institution),
            // No mixpanelToken and no eventsApiKey/eventsApiUrl on purpose: an
            // admin impersonating a member must not emit consumer analytics.
            // initMixpanel() is likewise never called (skipAnalytics below),
            // which leaves core's events controller unconfigured — do not add
            // either here.
            config: new Config({
                apiKey: boot.apiKey,
                environment: boot.environment,
                overrideServiceUrl: boot.serviceUrl,
                // Flag-derived, same as the whitelabel hosts: core's default is
                // `true`, which offered manual uploads to members whose FI has
                // the flag off.
                manualUploadEnabled: featureFlags.manual_uploads ?? false,
            }),
            dataStore: new ClockoutDataStore(),
            user: new User({
                clientId: boot.clientId,
                ssn: '00000000',
                memberId: identity.identifier || null,
                name,
                loginName: identity.logonName || null,
                email: identity.email || null,
                phoneNumber: identity.phoneNumber || null,
            }),
            style: new StyleConfig(boot.style || {}),
            skipFonts: true,
            skipAnalytics: true,
        });
        // bootstrapClockout points core's storage seam at rn-ui's AsyncStorage,
        // which on web is the admin's own localStorage. Put the in-memory store
        // back. This must stay synchronous with the call above: with skipFonts
        // nothing in the bootstrap awaits, so no core code runs in between.
        installOpenSsoStorage();

        booted
            .then(() => {
                if (active) setIsReady(true);
            })
            .catch((err) => {
                if (active) setBootError(err);
            });

        return () => {
            active = false;
        };
    }, [boot]);

    // Rethrown during render so a failed boot reaches the host's error handling
    // the way a throw from this effect did before the bootstrap was async.
    if (bootError) throw bootError;
    if (!isReady) return <SplashScreen />;

    const handleNavigationReady = () => RNNavigationController.getInstance().onNavigationReady();

    return (
        <SafeAreaProvider style={{ flex: 1 }}>
            <NavigationContainer ref={navigationRef} onReady={handleNavigationReady}>
                {/* rn-ui's default route table: Onboarding (the gate, sole
                entrypoint), Advance, Complete, Activity, Jobs, Payroll,
                ReauthMfa and ServiceUnavailable. */}
                <ClockoutStack />
            </NavigationContainer>
        </SafeAreaProvider>
    );
}
